import { Alert, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, Snackbar, Switch, Typography } from "@mui/material";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import { useState } from "react";
import ConfigUtils from "../../utils/Config";
import ConfigService from "../../services/ConfigService";
import { CONFIGTYPES } from "../../types/Config";
import { useNavigate } from "react-router-dom";
import * as constantes from "../../common/Constantes";
import IAlert from "../../types/Alert";
import { IAgendaConfig } from "../../types/AgendaConfig";

const ConfigAgenda = () => {
    const history = useNavigate();

    const defaultAgendaConfig: IAgendaConfig = {
      workingHours: { start: 8, end: 22 },
      calendarLayout: "no-overlap",
    };

    const initialAgendaConfig =
    ConfigUtils.getConfigAgenda() ?? defaultAgendaConfig;


    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [overlapCalendar, setOverlapCalendar] = useState<Boolean>(ConfigUtils.getOverlapCalendar() ?? false);
    const [confirmOverlap, setConfirmOverlap] = useState(false);
    const [pendingOverlapValue, setPendingOverlapValue] = useState<boolean | null>(null);
    const [loadingOverlap, setLoadingOverlap] = useState(false);

    const [agendaConfig, setAgendaConfig] = useState(initialAgendaConfig);
    const [agendaDraft, setAgendaDraft] = useState(initialAgendaConfig);

    const [loadingAgenda, setLoadingAgenda] = useState(false);

    const hasChanges = JSON.stringify(agendaDraft) !== JSON.stringify(agendaConfig);

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const updateOverlapConfig = async (enabled: boolean) => {
      try {
        setLoadingOverlap(true);
        await ConfigService.updateOverlap(ConfigUtils.getUserId(), { enabled });
        setOverlapCalendar(enabled);
        setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
        ConfigUtils.editConfig(CONFIGTYPES.overlap, enabled);
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "Config.Component.updateOverlapConfig",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingOverlap(false);
      }
    };

    const updateAgendaConfig = async () => {
      if (
        agendaDraft.workingHours.start >=
        agendaDraft.workingHours.end
      ) {
        setAlert({
          open: true,
          severity: "error",
          msg: "La hora de inicio debe ser anterior a la hora de fin.",
        });
      
        return;
      }

      setLoadingAgenda(true);

      try {
        await ConfigService.updateAgendaConfig(ConfigUtils.getUserId(), agendaDraft);
        setAgendaConfig(agendaDraft);
        setAgendaDraft(agendaDraft);
        setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
        ConfigUtils.editConfig(CONFIGTYPES.agenda, agendaDraft);
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "Config.Component.updateAgendaConfig",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingAgenda(false);
      }
    };

    try {
        return (
          <Grid container spacing={3} sx={{ p: { xs: 2, md: 4 } }}>
        
            {/* ========================= */}
            {/* HEADER */}
            {/* ========================= */}
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight={700}>
                Configuración de Agenda
              </Typography>
            </Grid>

            {/* ========================= */}
            {/* SOLAPAMIENTO */}
            {/* ========================= */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  Solapamiento de citas
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  Permite el solapamiento de varios pacientes con el mismo profesional
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={overlapCalendar as boolean}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                    
                        if (newValue) {
                          setPendingOverlapValue(newValue);
                          setConfirmOverlap(true);
                        } else {
                          updateOverlapConfig(newValue);
                        }
                      }}
                    />
                  }
                  label={
                    loadingOverlap
                      ? "Guardando..."
                      : overlapCalendar
                        ? "Activados"
                        : "Desactivados"
                  }
                />
              </Card>
            </Grid>
              
            {/* ========================= */}
            {/* CONFIG AGENDA */}
            {/* ========================= */}
            <Grid item xs={12}>
              <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                  Visualización de la agenda
                </Typography>

                {/* ========================= */}
                {/* HORARIO */}
                {/* ========================= */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Horario
                </Typography>
              
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption">Hora inicio</Typography>
              
                    <select
                      value={agendaDraft.workingHours.start}
                      onChange={(e) =>
                        setAgendaDraft((prev) => ({
                          ...prev,
                          workingHours: {
                            ...prev.workingHours,
                            start: Number(e.target.value),
                          },
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 8,
                        border: "1px solid #ddd",
                      }}
                    >
                      {Array.from(
                        { length: agendaDraft.workingHours.end },
                        (_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, "0")}:00
                          </option>
                        )
                      )}
                    </select>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption">Hora fin</Typography>
                  
                    <select
                      value={agendaDraft.workingHours.end}
                      onChange={(e) =>
                        setAgendaDraft((prev) => ({
                          ...prev,
                          workingHours: {
                            ...prev.workingHours,
                            end: Number(e.target.value),
                          },
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: 10,
                        borderRadius: 8,
                        border: "1px solid #ddd",
                      }}
                    >
                      {Array.from(
                        {
                          length: 24 - (agendaDraft.workingHours.start + 1),
                        },
                        (_, i) => agendaDraft.workingHours.start + i + 1
                      ).map((hour) => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, "0")}:00
                        </option>
                      ))}
                    </select>
                  </Grid>
                </Grid>

                {/* ========================= */}
                {/* FORMATO EVENTOS */}
                {/* ========================= */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Visualización de eventos
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={agendaDraft.calendarLayout === 'overlap'}
                          onChange={(e) =>
                            setAgendaDraft((prev) => ({
                              ...prev,
                              calendarLayout: e.target.checked
                                ? 'overlap'
                                : 'no-overlap',
                            }))
                          }
                        />
                      }
                      label={
                        agendaDraft.calendarLayout === 'overlap'
                          ? 'Las citas pueden superponerse visualmente'
                          : 'Las citas se organizan en columnas separadas'
                      }
                    />
                  </Grid>
                </Grid>
                  
              </Card>



              <Box
                display="flex"
                justifyContent="flex-end"
                mt={4}
              >
                <Button
                  variant="contained"
                  disabled={!hasChanges || loadingAgenda}
                  onClick={updateAgendaConfig}
                >
                  Guardar cambios
                </Button>
              </Box>

            </Grid>
              
            {/* ========================= */}
            {/* DIALOG OVERLAP */}
            {/* ========================= */}
            <Dialog open={confirmOverlap} onClose={() => setConfirmOverlap(false)}>
              <DialogTitle>Activar solapamientos</DialogTitle>
              
              <DialogContent>
                <DialogContentText>
                  Si activas los solapamientos, podrás crear varias citas a la misma hora
                  para el mismo profesional.
                </DialogContentText>
              </DialogContent>
              
              <DialogActions>
                <Button onClick={() => setConfirmOverlap(false)}>
                  Cancelar
                </Button>
              
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => {
                    if (pendingOverlapValue !== null) {
                      updateOverlapConfig(pendingOverlapValue);
                    }
                
                    setConfirmOverlap(false);
                    setPendingOverlapValue(null);
                  }}
                >
                  Activar
                </Button>
              </DialogActions>
            </Dialog>
              
            {/* ========================= */}
            {/* SNACKBAR */}
            {/* ========================= */}
            <Snackbar
              open={alert.open}
              autoHideDuration={6000}
              onClose={handleCloseAlert}
            >
              <Alert
                variant="filled"
                onClose={handleCloseAlert}
                severity={alert.severity}
                sx={{ width: "100%" }}
              >
                {alert.msg}
              </Alert>
            </Snackbar>
          </Grid>
        );
        } catch (e: any) {
        const error: ILog = { method: 'ConfigAgenda.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default ConfigAgenda;